import React, { RefObject, useEffect, useId, useRef } from 'react';

const AIR = 'M 203 238 Q 520 229 832 257';
const RETURN = 'M 810 244 Q 520 216 203 238';
// Image coordinates: radio → mast → roof entry → wall → router.
const CABLE = 'M 916 278 C 922 300 925 326 925 365 L 925 425 Q 925 441 939 447 L 847 568 Q 839 571 840 589 L 842 650 Q 842 657 849 663 L 870 683 Q 874 687 873 698 L 873 1055 Q 877 1104 824 1106 L 789 1104';

const DraftScrollBackground: React.FC<{ pageRef: RefObject<HTMLDivElement> }> = ({ pageRef }) => {
  const sceneRef = useRef<HTMLDivElement>(null);
  const id = useId().replace(/:/g, '');

  useEffect(() => {
    const scene = sceneRef.current;
    const page = pageRef.current;
    if (!scene || !page) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const viewportHeight = window.innerHeight;
      const width = Math.max(window.innerWidth, viewportHeight * 0.9);
      const height = width * 1536 / 1024;
      const bounds = page.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, -bounds.top / Math.max(1, bounds.height - viewportHeight)));
      scene.style.width = `${width}px`;
      scene.style.height = `${height}px`;
      scene.style.transform = `translate3d(${(window.innerWidth - width) * 0.8}px, ${-(height - viewportHeight) * progress}px, 0)`;
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const observer = new ResizeObserver(schedule);
    observer.observe(page);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    update();
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      cancelAnimationFrame(frame);
    };
  }, [pageRef]);

  return (
    <div className="draft-scene-viewport" aria-hidden="true">
      <div ref={sceneRef} className="draft-scene">
        <img src={`${process.env.PUBLIC_URL}/images/draft/roof-to-room-v1.webp`} alt="" fetchPriority="high" />
        <svg viewBox="0 0 1024 1536" className="draft-scene-signals">
          <defs>
            <filter id={`${id}-glow`} x="-100%" y="-100%" width="300%" height="300%"><feGaussianBlur stdDeviation="2.5" /></filter>
            <linearGradient id={`${id}-pulse`}>
              <stop stopColor="#28aaff" stopOpacity="0" />
              <stop offset="0.7" stopColor="#60d2ff" />
              <stop offset="1" stopColor="#e4faff" />
            </linearGradient>
          </defs>
          <circle cx="203" cy="238" r="5" fill="#45c6ff" filter={`url(#${id}-glow)`} />
          <circle cx="203" cy="238" r="1.8" fill="#ddf8ff" />
          {[false, true].map(returning => Array.from({ length: 7 }, (_, i) => (
            <g key={`${returning}-${i}`} className="draft-air-packet" style={{ offsetPath: `path('${returning ? RETURN : AIR}')`, animationDelay: `${-i * 0.5 - (returning ? 0.25 : 0)}s` }}>
              <rect x="-18" y="-2.5" width="21" height="5" rx="2.5" fill="#27b8ff" filter={`url(#${id}-glow)`} />
              <rect x="-16" y="-1.1" width="18" height="2.2" rx="1.1" fill={`url(#${id}-pulse)`} />
            </g>
          )))}
          <g fill="none" strokeLinecap="round">
            <path d={CABLE} stroke="#121212" strokeWidth="2.5" />
            <path d={CABLE} className="draft-cable-packet" stroke="#22b5ff" strokeWidth="5" filter={`url(#${id}-glow)`} />
            <path d={CABLE} className="draft-cable-packet" stroke="#a5edff" strokeWidth="1.5" />
            <g stroke="#53caff" strokeWidth="1.4" className="draft-router-wifi">
              <path d="M 746 1032 Q 765 1015 784 1032 M 751 1038 Q 765 1025 779 1038 M 757 1044 Q 765 1037 773 1044" />
            </g>
          </g>
        </svg>
      </div>
      <div className="draft-scene-shade" />
    </div>
  );
};

export default DraftScrollBackground;
